import type { DecodeResult } from '@/pages/qr-scanner/scannerModel'
import type { ScannerMode } from '@/pages/qr-scanner/scannerModel'
import type { ScannerWorkerRequest, ScannerWorkerResponse } from '@/pages/qr-scanner/scannerWorkerProtocol'

export interface ScannerDecoder {
  decode: (input: Blob | ImageData) => Promise<DecodeResult>
  terminate: () => void
}

export interface ScannerWorkerLike {
  onmessage: ((event: MessageEvent<ScannerWorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => void) | null
  onmessageerror: ((event: MessageEvent<unknown>) => void) | null
  postMessage: (message: ScannerWorkerRequest) => void
  terminate: () => void
}

export type ScannerWorkerFactory = () => ScannerWorkerLike

export class ScannerDecoderCancelledError extends Error {}
export class ScannerDecoderBusyError extends Error {}
export class ScannerDecoderWorkerError extends Error {}

export function createScannerDecoder(
  workerFactory: ScannerWorkerFactory = createProductionWorker,
  mode: ScannerMode = 'qr',
): ScannerDecoder {
  const worker = workerFactory()
  let nextRequestId = 1
  let terminated = false
  let workerStopped = false
  let workerFailure: ScannerDecoderWorkerError | null = null
  const pending = new Map<number, {
    resolve: (result: DecodeResult) => void
    reject: (cause: unknown) => void
  }>()

  worker.onmessage = ({ data }) => {
    const request = pending.get(data.requestId)
    if (!request || terminated) return
    if (data.type === 'fatal') {
      failWorker(new ScannerDecoderWorkerError('scanner_worker_wasm_failed'))
      return
    }
    pending.delete(data.requestId)
    if (data.type === 'result') request.resolve(data.result)
    else request.reject(new Error('scanner_worker_decode_failed'))
  }
  worker.onerror = () => {
    failWorker(new ScannerDecoderWorkerError('scanner_worker_failed'))
  }
  worker.onmessageerror = () => {
    failWorker(new ScannerDecoderWorkerError('scanner_worker_message_failed'))
  }

  function stopWorker(): void {
    if (workerStopped) return
    workerStopped = true
    worker.terminate()
  }

  function failWorker(cause: ScannerDecoderWorkerError): void {
    if (terminated || workerFailure) return
    workerFailure = cause
    stopWorker()
    for (const request of pending.values()) request.reject(cause)
    pending.clear()
  }

  return {
    decode(input) {
      if (terminated) return Promise.reject(new ScannerDecoderCancelledError())
      if (workerFailure) return Promise.reject(workerFailure)
      if (pending.size > 0) return Promise.reject(new ScannerDecoderBusyError())
      const requestId = nextRequestId
      nextRequestId += 1
      return new Promise((resolve, reject) => {
        pending.set(requestId, { resolve, reject })
        worker.postMessage({ type: 'decode', requestId, input, mode })
      })
    },
    terminate() {
      if (terminated) return
      terminated = true
      stopWorker()
      for (const request of pending.values()) request.reject(new ScannerDecoderCancelledError())
      pending.clear()
    },
  }
}

function createProductionWorker(): ScannerWorkerLike {
  return new Worker(new URL('./scannerWorker.ts', import.meta.url), { type: 'module' })
}
